// import components
import { ControlledSelect } from 'components/shared/ControlledFields';
import { IconTooltip } from 'components/shared/IconTooltip';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import ControlledCheckbox from 'components/shared/ControlledFields/ControlledCheckbox';
import ControlledRadioGroup from 'components/shared/ControlledFields/ControlledRadioGroup';
import { Typography, Box, Divider, FormLabel, Stack } from '@mui/material';

// import external libraries
import { useTranslation } from 'react-i18next';

const MatchContent = ({
  control,
  getValues,
  selfMatchOptions,
  selfMatch,
  selfMatchRadioOptions,
  selfMatchStaticRadioOptions
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'selfMatch' });

  const renderRadioGroups = (groups, options) =>
    groups.map(({ name, label }) => (
      <ControlledRadioGroup
        key={name}
        control={control}
        name={name}
        label={label}
        options={options}
        direction="row"
        inline
      />
    ));

  const SectionHeader = ({ titleKey, tooltipKey }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        marginTop: 3,
        marginBottom: 1
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t(titleKey)}
      </Typography>
      <IconTooltip fontSize="small" title={t(tooltipKey)} placement="top-end" />
    </Box>
  );

  const CategorySection = ({ label, children }) => (
    <Box sx={{ marginTop: 3 }}>
      <FormLabel
        sx={{
          color: 'text.primary',
          ml: -1,
          mb: 1.25
        }}
      >
        {label}:
      </FormLabel>
      <Box sx={{ paddingLeft: 5 }}>{children}</Box>
    </Box>
  );

  const publishGroups = [
    { name: 'self_match_automatic_publish_own_action', label: t('typeOwn') },
    {
      name: 'self_match_automatic_publish_shared_action',
      label: t('typeShared')
    },
    {
      name: 'self_match_automatic_publish_preference1_action',
      label: t('planUnitPref1')
    },
    {
      name: 'self_match_automatic_publish_preference2_action',
      label: t('planUnitPref2')
    }
  ];

  const publishCheckboxes = [
    {
      name: 'self_match_automatic_publish_function_preference1',
      label: t('functionPref1')
    },
    {
      name: 'self_match_automatic_publish_function_preference2',
      label: t('functionPref2')
    }
  ];

  const visibleGroups = [
    { name: 'self_match_automatic_visible_own_action', label: t('typeOwn') },
    {
      name: 'self_match_automatic_visible_shared_action',
      label: t('typeShared')
    },
    {
      name: 'self_match_automatic_visible_preference1_action',
      label: t('planUnitPref1')
    },
    {
      name: 'self_match_automatic_visible_preference2_action',
      label: t('planUnitPref2')
    }
  ];

  const visibleCheckboxes = [
    {
      name: 'self_match_automatic_visible_function_preference1',
      label: t('functionPref1')
    },
    {
      name: 'self_match_automatic_visible_function_preference2',
      label: t('functionPref2')
    }
  ];

  const flexGroups = [
    { name: 'self_match_flex_preference1_action', label: t('flex') },
    {
      name: 'self_match_flex_deployableasflexpreference1_action',
      label: t('deployableAsFlex')
    },
    { name: 'self_match_flex_flex_action', label: t('flex') },
    {
      name: 'self_match_flex_deployableasflex_action',
      label: t('deployableAsFlex')
    }
  ];

  const flexCheckboxes = [
    {
      name: 'self_match_flex_function_preference1',
      label: t('functionPref1')
    },
    {
      name: 'self_match_flex_function_preference2',
      label: t('functionPref2')
    }
  ];

  return (
    <>
      <Typography variant="h6">{t('title')}</Typography>
      <ControlledCheckbox
        control={control}
        name="self_match_roster_match_connect"
        label={t('roster_match_connect')}
      />
      <MultiStepForm.Section fullWidth title={t('section1')}>
        <ControlledSelect
          control={control}
          name="self_match"
          label={t('section1')}
          options={selfMatchOptions(t)}
        />
        <ControlledCheckbox
          control={control}
          name="self_match_ignore_negative_balance"
          label={t('ignore_negative_balance')}
        />

        {getValues('self_match') !== selfMatch.NO_SELF_MATCH && (
          <>
            <SectionHeader titleKey="publish" tooltipKey="tooltipPublish" />
            <CategorySection label={t('planUnitSubtitle')}>
              {renderRadioGroups(publishGroups, selfMatchRadioOptions)}
            </CategorySection>
            <CategorySection label={t('functions')}>
              <Stack>
                {publishCheckboxes.map(({ name, label }) => (
                  <ControlledCheckbox
                    key={name}
                    control={control}
                    name={name}
                    label={label}
                  />
                ))}
              </Stack>
            </CategorySection>

            <Divider sx={{ marginY: 3 }} />

            <SectionHeader titleKey="visible" tooltipKey="tooltipVisible" />
            <CategorySection label={t('planUnitSubtitle')}>
              {renderRadioGroups(visibleGroups, selfMatchRadioOptions)}
            </CategorySection>
            <CategorySection label={t('functions')}>
              <Stack>
                {visibleCheckboxes.map(({ name, label }) => (
                  <ControlledCheckbox
                    key={name}
                    control={control}
                    name={name}
                    label={label}
                  />
                ))}
              </Stack>
            </CategorySection>
          </>
        )}
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('section2')}>
        <ControlledCheckbox
          control={control}
          name="self_match_flex_match_connect"
          label={t('flex_match_connect')}
        />

        <CategorySection label={`${t('planUnitPref1')}`}>
          {renderRadioGroups(
            flexGroups.slice(0, 2),
            selfMatchStaticRadioOptions(t)
          )}
        </CategorySection>

        <CategorySection label={`${t('allPlanUnits')}`}>
          {renderRadioGroups(
            flexGroups.slice(2),
            selfMatchStaticRadioOptions(t)
          )}
        </CategorySection>

        <CategorySection label={t('functions')}>
          <Stack>
            {flexCheckboxes.map(({ name, label }) => (
              <ControlledCheckbox
                key={name}
                control={control}
                name={name}
                label={label}
              />
            ))}
          </Stack>
        </CategorySection>
      </MultiStepForm.Section>
    </>
  );
};

export default MatchContent;
